import { faFilter, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef } from 'react';
import { Animated, ScrollView, TouchableOpacity } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MapPolygonDropdown from '../../../components/MapPolygonDropdown';
import Badge from '../../../components/Badge';
import { useAuth } from '../../../context/Auth';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';

interface Props {
    polygons: MapPolygon[];
    setPolygons: React.Dispatch<React.SetStateAction<MapPolygon[]>>;
    actingBodyId: number;
    setActingBodyId: React.Dispatch<React.SetStateAction<number>>;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const defaultIconSize = 45;

export default function MapFilter({ polygons, setPolygons, actingBodyId, setActingBodyId, isOpen, setIsOpen }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const mapWidth = useRef(new Animated.Value(defaultIconSize)).current;
    const mapHeight = useRef(new Animated.Value(defaultIconSize)).current;

    useEffect(() => {
        const targetWidth = isOpen ? 300 : defaultIconSize;
        const targetHeight = isOpen ? 450 : defaultIconSize;

        Animated.parallel([
            Animated.timing(mapWidth, {
                toValue: targetWidth,
                duration: 300,
                useNativeDriver: false,
            }),
            Animated.timing(mapHeight, {
                toValue: targetHeight,
                duration: 300,
                useNativeDriver: false,
            }),
        ]).start();
    }, [isOpen]);

    function activeFilters() {
        let count = 0;

        if (polygons.length) {
            count += 1;
        }

        if (actingBodyId != 0) {
            count += 1;
        }

        return count;
    }

    return (
        <Animated.View style={[styles.mapContainer, { width: mapWidth, height: mapHeight, zIndex: 10 }]}>
            <ScrollView style={styles.filtersPositioning(isOpen)} contentContainerStyle={styles.filtersContent} >
                <MapPolygonDropdown
                    dropDownDirection='BOTTOM'
                    setPolygons={setPolygons}
                    zIndex={3}
                />
                {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences) &&
                    <ActingBodiesDropdown includeAllOption={true} value={actingBodyId} setValue={setActingBodyId} zIndex={2} />
                }
            </ScrollView>

            <TouchableOpacity style={styles.mapButton} onPress={() => setIsOpen(!isOpen)}>
                {!isOpen &&
                    <Badge
                        mode='ROUNDED'
                        style={styles.badge}
                        size='small'
                        backgroundColor={theme.colors.primary['500']}
                        textColor={theme.colors.primaryButton.text}
                    >
                        {activeFilters()}
                    </Badge>
                }
                {isOpen
                    ? <FontAwesomeIcon color={theme.colors.primaryButton.background} icon={faDown as IconDefinition} />
                    : <FontAwesomeIcon color={theme.colors.primaryButton.background} icon={faFilter} />}
            </TouchableOpacity>
        </Animated.View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    filtersPositioning: (isVisible: boolean) => ({
        opacity: isVisible ? 100 : 0,
        marginLeft: 45,
        padding: 8,
    }),
    filtersContent: {
        flexDirection: 'column',
        gap: 12
    },
    badge: {
        position: 'absolute',
        zIndex: 1000,
        top: -6,
        right: -6,

    },
    mapContainer: {
        borderRadius: 8,
        shadowColor: 'black',
        shadowOffset: {
            height: 5,
            width: -4
        },
        shadowOpacity: 0.4,
        shadowRadius: 12,
        backgroundColor: theme.colors.container.background,
    },
    mapButton: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: defaultIconSize,
        height: defaultIconSize,
        justifyContent: 'center',
        backgroundColor: theme.colors.primaryButton.text,
        borderRadius: 8,
    },
}));
